/**
 * Regelversion 11: Demokratie unter Druck, Widerstand und Sturz (Etappen C und D).
 *
 * Das Spiel zeigt Mechanismen, statt sie zu feiern. Alles ist richtungsneutral: Die Eingriffe stehen
 * jeder Regierung offen, egal mit welchem Programm.
 *
 *  - Zustand der Demokratie (0–100, Start 85). Eingriffe in die Gewaltenteilung senken ihn: Verfassungsgericht
 *    umbauen, Rundfunk auf Linie bringen, Wahlrecht zuschneiden, Bundesanwaltschaft an die Leine,
 *    Gesetz gegen „ausländische Agenten“, Notstand. Jeder Eingriff bringt der Regierung etwas.
 *  - Gegenkräfte: Mehrheit im Bundestag (mit Abweichlern), Bundesrat, Koalitionspartner, Bundespräsident,
 *    Karlsruhe (solange das Gericht nicht umgebaut ist) und die EU (Rechtsstaatsverfahren, eingefrorene Mittel).
 *    Die Ewigkeitsklausel (Art. 79 Abs. 3 GG) macht die Abschaffung von Wahlen legal unmöglich:
 *    Der Notstand ist ein offener Verfassungsbruch.
 *  - Widerstand (0–100) steigt mit jedem Eingriff, mit schwacher Wirtschaft, sozialer Schere und Spaltung.
 *    Bei 25, 45, 65 und 80 eskaliert er (Demonstrationen, Generalstreik, Abweichler, Behörden verweigern),
 *    jedes Mal mit einer Entscheidung: nachgeben, verhandeln oder durchgreifen.
 *  - Sturz: friedliche Revolution, die eigene Partei lässt den Kanzler fallen, Generalstreik, Misstrauensvotum
 *    oder EU-Sanktionen. Danach Epilog, Neuwahl und ein Makel für die Partei (Opposition aus engine/opp11.ts).
 *  - Gelingt der Umbau (zwölf Monate Notstand oder eine Wahl mit Demokratiewert unter 20), endet die Partie
 *    mit dem Epilog „Die Republik ist gefallen“: kein Sieg, keine Orden, eigene Kategorie in der Bestenliste.
 *  - In der Opposition kann selten auch eine Computerregierung zu solchen Mitteln greifen.
 * Nur im Einzelspieler.
 */
import { S, log } from './state';
import { since } from './version';
import { lvOn, lvl, lvlHas } from './level12';
import { rnd } from './rng';
import { clamp, dateYear } from './util';
import { NEED } from '../content/consts';
import { STATES, BRV } from '../content/states';
import { OUTLETS } from '../content/parties';
import { PA, PN } from './rules';
import { coalSeats, electionResult } from './election';
import { press } from './media';
import { giniNow } from './fair';
import { toast, poll } from './govern';

export const demOn = (): boolean => since(11) && !S.mp;
const A = () => S as any;

export type DemStep = { id: string; n: string; d: string; sev: number; need: 'bt' | 'br' | 'none'; eff: string };
export const STEPS: DemStep[] = [
  { id: 'gericht', n: 'Verfassungsgericht umbauen', sev: 12, need: 'bt', d: 'Zusätzliche Richterstellen in Karlsruhe, besetzt mit einfacher Mehrheit statt mit zwei Dritteln.', eff: 'Klagen in Karlsruhe haben kaum noch Erfolg, auch gegen weitere Eingriffe.' },
  { id: 'rundfunk', n: 'Rundfunk auf Linie bringen', sev: 8, need: 'br', d: 'Neue Staatsverträge: Die Regierung besetzt Intendanz und Rundfunkräte mit Vertrauten.', eff: 'Kritische Berichte wirken nur noch halb so stark.' },
  { id: 'wahlrecht', n: 'Wahlrecht zuschneiden', sev: 10, need: 'bt', d: 'Wahlkreise werden neu zugeschnitten, die Sperrklausel steigt auf acht Prozent.', eff: 'Bei Bundestagswahlen bringt der Zuschnitt rund 2,5 Punkte.' },
  { id: 'justiz', n: 'Bundesanwaltschaft an die Leine', sev: 10, need: 'bt', d: 'Die Bundesanwaltschaft wird weisungsgebunden, Ermittlungen richten sich gegen Oppositionspolitiker.', eff: 'Die Opposition ist geschwächt, eure Basis jubelt, die Spaltung wächst.' },
  { id: 'ngo', n: 'Gesetz gegen „ausländische Agenten“', sev: 8, need: 'bt', d: 'Vereine mit Geld aus dem Ausland müssen sich als Agenten registrieren, Versammlungen werden leichter verboten.', eff: 'Proteste lassen sich schwerer organisieren: Der Widerstand wächst langsamer.' },
  { id: 'notstand', n: 'Notstand ausrufen, Wahl aussetzen', sev: 25, need: 'none', d: 'Die Regierung ruft den Notstand aus und verschiebt die nächste Bundestagswahl auf unbestimmte Zeit. Das Grundgesetz lässt das nicht zu: Es ist ein offener Verfassungsbruch.', eff: 'Keine Wahl mehr. Hält das Regime zwölf Monate, ist die Republik gefallen.' },
];
export const STEP: Record<string, DemStep> = Object.fromEntries(STEPS.map((x) => [x.id, x]));
export const LEVELS = [25, 45, 65, 80];

export type Dem = {
  v: number; min: number; res: number; done: Record<string, number>; pend: { id: string; m: number }[];
  card: { lvl: number; m: number } | null; lvl: number; heat: number; lock: number; eu: number; euF: boolean;
  pres: Record<string, number>; log: { m: number; t: string }[]; gs: number; not: number | null; fall: { how: string; m: number } | null; hard?: number;
};
export const demS = (): Dem | null => A().dem || null;
export const dem = (): Dem => (A().dem ??= { v: 85, min: 85, res: 0, done: {}, pend: [], card: null, lvl: 0, heat: 0, lock: 0, eu: 0, euF: false, pres: {}, log: [], gs: 0, not: null, fall: null });
export const demV = (): number => demS()?.v ?? 85;
export const demLabel = (v = demV()): string => (v >= 75 ? 'gefestigt' : v >= 55 ? 'beschädigt' : v >= 35 ? 'ausgehöhlt' : v >= 20 ? 'autoritär' : 'Diktatur');
export const resLabel = (r: number): string => (r < 25 ? 'ruhig' : r < 45 ? 'Großdemonstrationen' : r < 65 ? 'Generalstreik' : r < 80 ? 'Abweichler in Fraktion und Kabinett' : 'Behörden verweigern den Dienst');
const note = (t: string) => { const D = dem(); D.log.push({ m: S.m, t }); };
/** Ist das eine Partie, die in die eigene Kategorie der Bestenliste gehört? */
export const isAuto = (s: any = S): boolean => (s?.dem?.min ?? 85) < 50;

// ── Hilfen für andere Module ──
/** Version 11: Wahlkreiszuschnitt bei Bundestagswahlen. */
export const demElect = (): number => (demS()?.done.wahlrecht != null ? 2.5 : 0);
/** Version 11: Rundfunk auf Linie, Kritik wirkt halb. */
export const demPress = (tone: number): number => (tone < 0 && demS()?.done.rundfunk != null ? tone * .5 : tone);
/** Version 11: Umgebautes Verfassungsgericht. */
export const courtCaptured = (): boolean => demS()?.done.gericht != null;
/** Am Ende der Legislatur: keine echte Wahl mehr? */
export const noElection = (): boolean => { const D = demS(); return !!D && demOn() && (D.not != null || D.v < 20); };

const brShareCoal = (): number => Object.keys(STATES).reduce((s, id) => s + (['YOU', ...S.partners].includes(S.gov[id]) ? STATES[id].v : 0), 0) / BRV;

/** Was spricht gerade gegen einen Eingriff? null = möglich. */
export function stepBlock(id: string): string | null {
  const D = demS(), X = STEP[id], v = D?.v ?? 85;
  if (!demOn() || !X) return 'nicht verfügbar';
  if (D?.done[id] != null) return 'schon umgesetzt';
  if (since(12) && !S.career.length) return 'Erst ab der zweiten Legislatur.'; // Version 12
  if (!lvlHas('macht')) return 'Erst auf der Stufe „Machtpolitik“.'; // Version 12: Erfahrungsstufe
  if (D && S.m < D.lock) return 'Ihr habt am Runden Tisch eine Pause zugesagt.';
  if (D && D.res >= 80) return 'Polizei und Behörden ziehen nicht mehr mit.';
  if (id === 'notstand' && v > 60) return 'Dafür ist die Demokratie noch zu stark. Erst müssen andere Schranken fallen.';
  if (D?.pres[id] != null && S.m - D.pres[id] < 6) return 'Der Bundespräsident hat gerade erst abgelehnt.';
  if (X.need !== 'none' && coalSeats() < NEED) return `Ihr habt keine eigene Mehrheit im Bundestag (${coalSeats()} von ${NEED}).`;
  if (X.need === 'br' && brShareCoal() <= .5) return 'Ihr habt keine Mehrheit im Bundesrat.';
  return null;
}

/** Spielzug: einen Eingriff versuchen. */
export function demStep(id: string) {
  if (S.phase !== 'govern' || stepBlock(id)) return;
  const D = dem(), X = STEP[id];
  // Koalitionspartner
  for (const p of S.partners.slice()) {
    if (id === 'notstand') { S.partners = S.partners.filter((x) => x !== p); S.minority = coalSeats() < NEED; log(`${PN(p)} verlässt die Regierung: „Bei einem Verfassungsbruch machen wir nicht mit.“`, 'bad'); continue; }
    if (S.coal < 50 + X.sev * 1.5) {
      S.coal = clamp(S.coal - 15, 0, 100); D.res = clamp(D.res + 3, 0, 100);
      log(`${PN(p)} verweigert sich: „${X.n}“ ist mit dem Koalitionspartner nicht zu machen.`, 'bad'); press(OUTLETS[3].n, `Koalitionskrach: ${PA(p)} stoppt „${X.n}“`, -1); return;
    }
    S.coal = clamp(S.coal - X.sev, 0, 100);
  }
  // Abweichler in der eigenen Fraktion
  if (X.need !== 'none') {
    const dev = Math.round((S.res.seats.YOU || 0) * clamp(.03 + (85 - D.v) * .002 + D.res * .001 + rnd() * .03, 0, .3));
    if (coalSeats() - dev < NEED) {
      D.res = clamp(D.res + 4, 0, 100); S.basis -= .5;
      log(`Abstimmung über „${X.n}“: ${dev} Abgeordnete der Regierung stimmen dagegen. Keine Mehrheit.`, 'bad'); press(OUTLETS[0].n, `Aufstand in der Fraktion: „${X.n}“ scheitert`, -1); return;
    }
    // Bundespräsident
    if (rnd() < clamp(.1 + (D.v - 40) / 120, .05, .45)) {
      D.pres[id] = S.m; D.res = clamp(D.res + 5, 0, 100);
      log(`Der Bundespräsident verweigert die Unterschrift unter „${X.n}“: Das Gesetz sei offenkundig verfassungswidrig.`, 'bad'); press(OUTLETS[0].n, `Bundespräsident stoppt „${X.n}“`, -1.2); toast(`Bundespräsident stoppt „${X.n}“`, 'bad'); return;
    }
  }
  apply(id);
}

function apply(id: string) {
  const D = dem(), X = STEP[id];
  D.v = clamp(D.v - X.sev, 0, 100); D.min = Math.min(D.min, D.v); D.done[id] = S.m;
  D.res = clamp(D.res + X.sev * 1.5 * (D.done.ngo != null && id !== 'ngo' ? .75 : 1), 0, 100); D.eu += X.sev;
  if (id === 'justiz') { S.basis += 1.5; A().polar = clamp((A().polar || 0) + 10, 0, 100); }
  if (id === 'notstand') D.not = S.m;
  if (id !== 'gericht' && D.done.gericht == null) D.pend.push({ id, m: S.m + 5 });
  const t = id === 'notstand' ? `Die Regierung ruft den Notstand aus. Die Bundestagswahl ist ausgesetzt.` : `„${X.n}“ ist beschlossen.`;
  log(`${t} Der Zustand der Demokratie sinkt auf ${Math.round(D.v)}.`, 'bad'); note(X.n);
  press(OUTLETS[0].n, id === 'notstand' ? 'Notstand: Regierung setzt Bundestagswahl aus' : `Kritik aus allen Lagern: „${X.n}“`, -1.5);
  if (!D.euF && D.eu >= 25) { D.euF = true; S.shockGr -= .3; log('Brüssel eröffnet ein Rechtsstaatsverfahren nach Artikel 7 und friert EU-Mittel für Deutschland ein.', 'bad'); press(OUTLETS[1].n, 'EU friert Milliarden für Deutschland ein', -1.2); note('EU-Rechtsstaatsverfahren'); }
}

function revert(id: string, why: string) {
  const D = dem(), X = STEP[id]; if (D.done[id] == null) return;
  delete D.done[id]; D.v = clamp(D.v + X.sev, 0, 85); D.pend = D.pend.filter((p) => p.id !== id);
  if (id === 'justiz') S.basis -= 1.5;
  if (id === 'notstand') D.not = null;
  log(`${why} „${X.n}“ ist aufgehoben. Der Zustand der Demokratie steigt auf ${Math.round(D.v)}.`, 'good'); note(`${X.n} aufgehoben`);
}

/** Entscheidung, wenn der Widerstand eine neue Stufe erreicht. */
export const CARD_TXT: Record<number, [string, string]> = {
  25: ['Großdemonstrationen', 'Hunderttausende gehen in allen großen Städten auf die Straße. Sie fordern, die Eingriffe zurückzunehmen.'],
  45: ['Generalstreik', 'Gewerkschaften, Verbände und Kirchen rufen zum Generalstreik auf. Bahnen, Häfen und Fabriken stehen still.'],
  65: ['Abweichler', 'In der eigenen Fraktion und im Kabinett wächst der Widerstand. Erste Minister drohen mit Rücktritt.'],
  80: ['Behörden verweigern sich', 'Polizeiführung, Bundesanwaltschaft und Teile der Verwaltung erklären, verfassungswidrige Anordnungen nicht mehr auszuführen.'],
};
export const CARD_OPTS: [string, string][] = [
  ['Nachgeben', 'Den letzten Eingriff zurücknehmen. Der Widerstand sinkt deutlich, eure Basis ist enttäuscht.'],
  ['Verhandeln', 'Runder Tisch mit Opposition und Zivilgesellschaft: ein Jahr keine weiteren Eingriffe. Der Widerstand sinkt etwas.'],
  ['Durchgreifen', 'Proteste auflösen, Rädelsführer festnehmen. Der Widerstand sinkt kurz und kommt stärker zurück, die Demokratie leidet weiter.'],
];
export function demCard(i: number) {
  const D = demS(); if (!D?.card) return; D.card = null;
  if (i === 0) {
    const last = Object.entries(D.done).reverse().sort((a, b) => b[1] - a[1])[0];
    if (last) revert(last[0], 'Die Regierung gibt nach:');
    D.res = clamp(D.res - 15, 0, 100); S.basis -= .5;
  } else if (i === 1) {
    D.res = clamp(D.res - 8, 0, 100); D.lock = S.m + 12; D.v = clamp(D.v + 2, 0, 85);
    for (const g in S.bonus) S.bonus[g] -= .3;
    log('Runder Tisch: Die Regierung sagt zu, ein Jahr lang keine weiteren Eingriffe vorzunehmen.'); note('Runder Tisch');
  } else {
    D.hard = (D.hard || 0) + 1; D.res = clamp(D.res - 12 * Math.pow(.7, D.hard - 1), 0, 100); D.heat = 10; D.v = clamp(D.v - 4, 0, 100); D.min = Math.min(D.min, D.v);
    A().polar = clamp((A().polar || 0) + 10, 0, 100); S.basis += .5;
    log('Die Regierung greift durch: Demonstrationen werden aufgelöst, Organisatoren festgenommen.', 'bad'); press(OUTLETS[3].n, 'Polizei räumt Plätze, Festnahmen im ganzen Land', -1.5); note('Durchgreifen');
  }
}

function fall(how: string) {
  const D = dem(); D.fall = { how, m: S.m }; S.end = 'sturz';
  const txt: Record<string, string> = {
    revolution: 'Eine friedliche Revolution: Millionen auf den Straßen, die Regierung tritt zurück.',
    partei: 'Die eigene Partei lässt den Kanzler fallen, um sich zu retten.',
    streik: 'Der Generalstreik legt das Land lahm. Die Regierung gibt auf.',
    eu: 'Sanktionen und eingefrorene Mittel treiben das Land in die Krise. Die Regierung tritt zurück.',
  };
  log(txt[how] || 'Die Regierung ist gestürzt.', 'bad'); note('Sturz der Regierung');
  press(OUTLETS[0].n, 'Die Regierung ist gestürzt', -2);
}

/** Monatlich in der Regierungszeit. Gibt true zurück, wenn etwas Wichtiges passiert ist. */
export function demTick(): boolean {
  const D = demS(); if (!D || !demOn() || S.phase !== 'govern') return false;
  let att = false;
  const active = Object.keys(D.done).length > 0;
  let g = (85 - D.v) * .004 + Math.max(0, giniNow() - 30) * .05 + Math.max(0, S.al - 7) * .1 + (A().polar || 0) * .004 + D.heat * .35 - (active ? .15 : .6);
  if (D.done.ngo != null && g > 0) g *= .75;
  if (D.not != null) g += 2.5;
  g -= clamp((poll() - 35) * .03, -.3, .45); // eine starke eigene Basis dämpft den Widerstand
  D.res = clamp(D.res + g, 0, 100); D.heat = Math.max(0, D.heat - 1);
  // Karlsruhe
  for (const p of D.pend.filter((x) => S.m >= x.m)) {
    D.pend = D.pend.filter((x) => x !== p);
    if (D.done[p.id] == null) continue;
    if (D.done.gericht != null) { log(`Karlsruhe lässt „${STEP[p.id].n}“ passieren. Das umgebaute Gericht stellt sich nicht mehr gegen die Regierung.`); continue; }
    if (p.id === 'notstand') { D.res = clamp(D.res + 15, 0, 100); log('Karlsruhe erklärt den Notstand für verfassungswidrig. Die Regierung ignoriert das Urteil.', 'bad'); note('Karlsruhe gegen den Notstand'); att = true; continue; }
    if (rnd() < .55) { revert(p.id, 'Karlsruhe greift ein:'); D.res = clamp(D.res + 3, 0, 100); toast(`Karlsruhe kippt „${STEP[p.id].n}“`, 'good'); att = true; }
    else log(`Karlsruhe weist die Klagen gegen „${STEP[p.id].n}“ ab.`);
  }
  if (D.euF) S.shockB -= .3;
  if (D.res >= 25) for (const k in S.bonus) S.bonus[k] -= .03;
  if (D.res >= 45) { S.shockGr -= .05; D.gs++; } else D.gs = 0;
  if (D.res >= 65) { S.basis -= .15; if (S.partners.length && rnd() < .1) S.coal = clamp(S.coal - 8, 0, 100); }
  const lvl = LEVELS.find((l) => l > D.lvl && D.res >= l);
  if (lvl) { D.lvl = lvl; D.card = { lvl, m: S.m }; note(CARD_TXT[lvl][0]); log(`${CARD_TXT[lvl][0]}: ${CARD_TXT[lvl][1]}`, 'bad'); return true; }
  if (D.res < LEVELS[0] - 5) D.lvl = 0; else { const back = [...LEVELS].reverse().find((l) => D.res >= l - 5); if (back != null && back < D.lvl) D.lvl = back; }
  // Sturz
  if (D.res >= 100) fall('revolution');
  else if (D.res >= 80 && rnd() < .12) fall('partei');
  else if (D.not != null && D.res >= 65 && rnd() < .07) fall('revolution');
  else if (D.gs >= 6 && D.res >= 65) fall('streik');
  else if (D.euF && S.gr < -1.5 && D.res >= 50 && rnd() < .1) fall('eu');
  else if (D.not == null && D.res >= 55 && S.partners.length && rnd() < .08) {
    const opp = (S.res.inP || []).filter((k: string) => k !== 'YOU');
    S.end = 'misstrauen'; S.endBy = opp; D.fall = { how: 'misstrauen', m: S.m }; note('Misstrauensvotum');
    log('Die Koalitionspartner wechseln die Seiten. Ein konstruktives Misstrauensvotum beendet die Regierung.', 'bad');
  } else if (D.not != null && S.m - D.not >= 12) { S.end = 'autokratie'; note('Die Republik ist gefallen'); }
  return att || S.end != null;
}

/** Nach einem Sturz: Die Institutionen werden wiederhergestellt. */
export function demRestore() {
  const D = demS(); if (!D) return;
  Object.assign(D, { v: 80, res: 0, done: {}, pend: [], card: null, lvl: 0, heat: 0, lock: 0, eu: 0, euF: false, pres: {}, gs: 0, not: null });
}

/** Neuwahl nach einem Sturz: Die eigene Partei bricht ein. Aufruf aus oppStart. */
export function sturzElection() {
  const x = clamp(S.elec * .45 + rnd() * 3, 3, 20);
  S.partners = [];
  const r: any = electionResult(x); S.res2 = r; S.final = x; S.elYear = dateYear(S.m);
}

// ── Computerregierung in der Opposition ──
/** Selten greift auch eine Computerregierung zu solchen Mitteln. Liefert den Eingriff oder null. */
export function aiDemStep(): DemStep | null {
  if (!demOn() || (lvOn() && lvl() < 3) || rnd() >= .005) return null; // Version 12: nicht auf Stufe 1 und 2
  const D = dem(), cand = STEPS.filter((x) => x.id !== 'notstand' && D.done[x.id] == null);
  if (!cand.length) return null;
  const X = cand[Math.floor(rnd() * cand.length)];
  D.v = clamp(D.v - X.sev, 0, 100); D.done[X.id] = S.m; note(`Regierung: ${X.n}`);
  return X;
}
/** Klage der Opposition gegen einen Eingriff war erfolgreich. */
export function aiDemUndo(id: string) { const D = demS(); if (!D || D.done[id] == null) return; delete D.done[id]; D.v = clamp(D.v + STEP[id].sev, 0, 85); note(`${STEP[id].n} gekippt`); }
